import React, { useState, useEffect, useRef, useId } from 'react';
import { getFixtureSheetsByTournament } from '../../../api/fixtureSheets';
import { formatKickoff } from '../../../lib/formatDate';

// How many upcoming match weeks to offer as starting points.
const WEEKS_SHOWN = 6;

/**
 * The earliest kick-off on a sheet, or null when the sheet carries no fixtures we
 * can read.
 *
 * Null rather than Infinity on purpose: "this week has not started" and "we cannot
 * tell" are different answers, and only the first one is safe to act on.
 */
function firstKickoff(sheet) {
  const times = (sheet.fixtures || [])
    .map((f) => new Date(f.startDateTime).getTime())
    .filter((t) => !Number.isNaN(t));
  return times.length > 0 ? Math.min(...times) : null;
}

/**
 * A week is a valid starting point only while its football is still ahead of it.
 *
 * The status filter alone is not enough. A tournament seeded with a whole real season
 * holds UPCOMING sheets for weeks that have already been played - migrations 057-060
 * seed every week of 2026/27, and by definition some of them are behind us. Offering
 * one produces a game whose week 1 deadline has already passed, which
 * GameWeekService.calculatePickDeadline then quietly resets to "half an hour from
 * now" - a game played against matches that have already kicked off.
 *
 * Sheets whose fixtures we cannot read are kept. Hiding a week because its payload
 * was thin would empty this dropdown with no way for anyone to tell why.
 */
function isStartable(sheet) {
  const kickoff = firstKickoff(sheet);
  return kickoff === null || kickoff > Date.now();
}

export const tournamentName = (tournament) =>
  tournament ? (tournament.name || tournament.tournamentName) : '';

/**
 * Pick a tournament and the week the game starts on — shared by the
 * create-game and create-mini-league wizards, and by "add a tournament"
 * on an existing league.
 *
 * Controlled: `value` is {tournamentId, startingMatchWeek} (both strings) and
 * every change is reported through onChange. Selecting a tournament loads its
 * playable weeks and auto-selects the earliest.
 *
 * `excludeTournamentIds` hides tournaments already registered to a league —
 * the backend rejects duplicates, so they must not be offerable.
 */
function TournamentStep({ tournaments, value, onChange, excludeTournamentIds = [] }) {
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [weeksLoading, setWeeksLoading] = useState(false);

  // Neither label wrapped its select and neither carried htmlFor, so neither was
  // associated with anything: a screen reader announced an unlabelled combo box, and
  // getByLabelText found nothing. useId rather than a literal because three wizards
  // render this component and ids have to be unique per instance.
  const fieldId = useId();
  const tournamentId = `${fieldId}-tournament`;
  const weekId = `${fieldId}-week`;

  // Keeps the effect below dependent on the tournament alone, so selecting a
  // week can't re-trigger the fetch.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  // Only weeks that can still be played are valid starting points: the tournament's
  // ACTIVE and UPCOMING fixture sheets — never weeks already locked or closed, and
  // never a week whose first match has already kicked off. See isStartable.
  useEffect(() => {
    if (!value.tournamentId) {
      setAvailableWeeks([]);
      onChangeRef.current({ tournamentId: '', startingMatchWeek: '' });
      return;
    }
    let cancelled = false;
    setWeeksLoading(true);
    getFixtureSheetsByTournament(value.tournamentId)
      .then((sheets) => {
        if (cancelled) return;
        const weeks = sheets
          .filter((s) => s.status === 'ACTIVE' || s.status === 'UPCOMING')
          .filter(isStartable)
          .sort((a, b) => a.matchWeek - b.matchWeek)
          .slice(0, WEEKS_SHOWN);
        setAvailableWeeks(weeks);
        onChangeRef.current({
          tournamentId: value.tournamentId,
          startingMatchWeek: weeks.length > 0 ? String(weeks[0].matchWeek) : '',
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error loading match weeks:', err);
        setAvailableWeeks([]);
        onChangeRef.current({ tournamentId: value.tournamentId, startingMatchWeek: '' });
      })
      .finally(() => {
        if (!cancelled) setWeeksLoading(false);
      });
    return () => { cancelled = true; };
  }, [value.tournamentId]);

  const selectableTournaments = tournaments.filter(
    (t) => !excludeTournamentIds.map(String).includes(String(t.id))
  );

  const weekLabel = (sheet) =>
    sheet.status === 'ACTIVE' ? `Week ${sheet.matchWeek} (current)` : `Week ${sheet.matchWeek}`;

  const selectedSheet = availableWeeks.find(
    (s) => String(s.matchWeek) === value.startingMatchWeek
  );
  const previewFixtures = (selectedSheet?.fixtures || [])
    .slice()
    .sort((a, b) => new Date(a.startDateTime) - new Date(b.startDateTime));

  return (
    <>
      <div className="form-group">
        <label htmlFor={tournamentId}>Tournament</label>
        <select
          id={tournamentId}
          value={value.tournamentId}
          onChange={(e) => onChange({ tournamentId: e.target.value, startingMatchWeek: '' })}
          required
        >
          <option value="">Select a tournament...</option>
          {selectableTournaments.map((t) => (
            <option key={t.id} value={t.id}>{tournamentName(t)}</option>
          ))}
        </select>
        {selectableTournaments.length === 0 && (
          <p className="helper-text">Every available tournament is already in this league</p>
        )}
      </div>

      <div className="form-group">
        <label htmlFor={weekId}>Starting Match Week</label>
        <select
          id={weekId}
          value={value.startingMatchWeek}
          onChange={(e) => onChange({ ...value, startingMatchWeek: e.target.value })}
          disabled={!value.tournamentId || weeksLoading || availableWeeks.length === 0}
          required
        >
          {!value.tournamentId ? (
            <option value="">Select a tournament first</option>
          ) : weeksLoading ? (
            <option value="">Loading match weeks...</option>
          ) : availableWeeks.length === 0 ? (
            <option value="">No upcoming match weeks available</option>
          ) : (
            availableWeeks.map((sheet) => (
              <option key={sheet.matchWeek} value={sheet.matchWeek}>
                {weekLabel(sheet)}
              </option>
            ))
          )}
        </select>
        <p className="helper-text">Only match weeks that haven't started yet can be picked</p>
      </div>

      {selectedSheet && previewFixtures.length > 0 && (
        <div className="week-preview">
          <div className="week-preview-header">
            Week {selectedSheet.matchWeek} · starts {formatKickoff(previewFixtures[0].startDateTime)} · {previewFixtures.length} fixtures
          </div>
          <div className="week-preview-list">
            {previewFixtures.map((f) => (
              <div key={f.id} className="week-preview-fixture">
                <span className="week-preview-teams">
                  {f.homeTeam?.name || f.homeTeam?.teamName} v {f.awayTeam?.name || f.awayTeam?.teamName}
                </span>
                <span className="week-preview-time">{formatKickoff(f.startDateTime)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default TournamentStep;
